import { Routes } from '@angular/router';
import { DashboardComponent } from '@app/pages/dashboard/dashboard.component';
import { NotfoundComponent } from '@app/pages/notfound/notfound.component';
import { onlyAuthUserGuard } from '@shared/guards/only-auth-user.guard';
import { supplierAdminGuard } from '@shared/guards/supplier-admin.guard';
import { NotAuthorizedComponent } from '@app/pages/notauthorized/not-authorized.component';
import { LoginComponent } from '@app/pages/login/login.component';
import { SalesReportLayoutComponent } from '@app/layout/component/sales-report-layout/sales-report-layout.component';
import { SalesReportComponent } from '@app/pages/dashboard/reports/sales-report/sales-report.component';
import { OtaReportComponent } from '@app/pages/dashboard/reports/ota-report/ota-report.component';
import { PriceMismatchComponent } from '@app/pages/dashboard/reports/price-mismatch/price-mismatch.component';
import { SalePerformanceComponent } from '@app/pages/dashboard/reports/sale-performance/sale-performance.component';
import { AccountingReportComponent } from '@app/pages/reports/accounting-report/accounting-report.component';
import { LayoutComponent } from '@app/layout/layout.component';
import { CmsContentListComponent } from '@app/pages/cms/cms-content-list/cms-content-list.component';
import { ContentCreateEditComponent } from '@app/pages/cms/content-create-edit/content-create-edit.component';
import { CmsComponent } from '@app/pages/cms/cms.component';
import { ProductManagementComponent } from '@app/pages/parameters/product-management/product-management.component';
import { AdditionalCostManagementComponent } from '@app/pages/parameters/additional-cost-management/additional-cost-management.component';
import { ParametersComponent } from '@app/pages/parameters/parameters.component';
import { ReportsComponent } from '@app/pages/reports/reports.component';
import { YearlyRevenueReportComponent } from '@app/pages/reports/yearly-revenue-report/yearly-revenue-report.component';
import { DefinitionsComponent } from '@app/pages/definitions/definitions.component';
import { UserDefinitionsComponent } from '@app/pages/definitions/user-definitions/user-definitions.component';
import { AirlineRulesDefinitionsComponent } from '@app/pages/definitions/airline-rules-definitions/airline-rules-definitions.component';
import { AncillaryManagementComponent } from '@app/pages/parameters/ancillary-management/ancillary-management.component';
import { AncillaryMarkupManagementComponent } from '@app/pages/parameters/ancillary-markup-management/ancillary-markup-management.component';
import { DiscountCouponComponent } from '@app/pages/parameters/discount-coupon/discount-coupon.component';
import { discountCouponGuard } from '@shared/guards/discount-coupon.guard';
import { ApisDefinitionsManagementComponent } from '@app/pages/definitions/apis-definitions-management/apis-definitions-management.component';
import { PaymentDefinitionsComponent } from '@app/pages/payment-definitions/payment-definitions.component';
import { TenantDefinitionComponent } from '@app/pages/payment-definitions/tenant-definition/tenant-definition.component';
import { TenantUserDefinitionComponent } from '@app/pages/payment-definitions/tenant-user-definition/tenant-user-definition.component';
import { PspDefinitionComponent } from '@app/pages/payment-definitions/psp-definition/psp-definition.component';
import { PspCurrencyComponent } from '@app/pages/payment-definitions/psp-currency/psp-currency.component';
import { PspSettingComponent } from '@app/pages/payment-definitions/psp-setting/psp-setting.component';
import { ProviderLoginInfoComponent } from '@app/pages/payment-definitions/provider-login-info/provider-login-info.component';
import { PspErrorCodeComponent } from '@app/pages/payment-definitions/psp-error-code/psp-error-code.component';
import { PaymentTransactionLogComponent } from '@app/pages/payment-definitions/payment-transaction-log/payment-transaction-log.component';

export const appRoutes: Routes = [
    {
        path: '',
        component: LayoutComponent,
        canActivateChild: [onlyAuthUserGuard, supplierAdminGuard],
        children: [
            { path: '', component: DashboardComponent },
            {
                path: 'reports',
                component: ReportsComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    { path: 'accounting-report', component: AccountingReportComponent },
                    { path: 'yearly-revenue-report', component: YearlyRevenueReportComponent },
                    {
                        path: 'quota-risk-reports',
                        loadComponent: () =>
                            import('@app/pages/reports/quota-risk-reports/quota-risk-reports.component').then(
                                (m) => m.QuotaRiskReportsComponent
                            )
                    },
                    {
                        path: 'quota-risk-report',
                        redirectTo: 'quota-risk-reports',
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'definitions',
                component: DefinitionsComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    {
                        path: 'user-definitions',
                        component: UserDefinitionsComponent
                    },
                    {
                        path: 'airline-rules-definitions',
                        component: AirlineRulesDefinitionsComponent
                    }
                ]
            },

            {
                path: 'parameters',
                component: ParametersComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    { path: 'product-management', component: ProductManagementComponent },
                    { path: 'additional-cost-management', component: AdditionalCostManagementComponent },
                    { path: 'ancillary-management', component: AncillaryManagementComponent },
                    { path: 'ancillary-markup-management', component: AncillaryMarkupManagementComponent },
                    { path: 'apis-definitions-management', component: ApisDefinitionsManagementComponent },
                    { path: 'discount-coupon', component: DiscountCouponComponent, canActivate: [discountCouponGuard] }
                ]
            },
            {
                path: 'configuration',
                component: ParametersComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    {
                        path: 'penalty-rule-definitions',
                        loadComponent: () =>
                            import('@app/pages/parameters/penalty-rule-definitions/penalty-rule-definitions.component').then(
                                (m) => m.PenaltyRuleDefinitionsComponent
                            )
                    }
                ]
            },
            {
                path: 'payment-definitions',
                component: PaymentDefinitionsComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    { path: 'tenant-definition', component: TenantDefinitionComponent },
                    { path: 'tenant-user-definition', component: TenantUserDefinitionComponent },
                    { path: 'psp-definition', component: PspDefinitionComponent },
                    { path: 'psp-currency', component: PspCurrencyComponent },
                    { path: 'psp-setting', component: PspSettingComponent },
                    { path: 'provider-login-info', component: ProviderLoginInfoComponent },
                    { path: 'psp-error-code', component: PspErrorCodeComponent },
                    { path: 'payment-transaction-log', component: PaymentTransactionLogComponent }
                ]
            },
            {
                path: 'cms',
                component: CmsComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    { path: '', component: CmsContentListComponent },
                    { path: 'create-edit', component: ContentCreateEditComponent },
                    { path: 'create-edit/:id', component: ContentCreateEditComponent }
                ]
            },
            {
                path: 'reports',
                component: SalesReportLayoutComponent,
                canActivateChild: [onlyAuthUserGuard],
                children: [
                    { path: 'sales-report', component: SalesReportComponent },
                    { path: 'ota-report', component: OtaReportComponent },
                    { path: 'price-mismatch', component: PriceMismatchComponent },
                    { path: 'sale-performance', component: SalePerformanceComponent }
                ]
            },
            {
                path: 'flights',
                loadComponent: () => import('@app/pages/flights/flight-list/flight-list.component').then((m) => m.FlightListComponent)
            },
            {
                path: 'hotels',
                children: [
                    {
                        path: 'hotel-booking-list',
                        loadComponent: () =>
                            import('@app/pages/hotels/hotel-booking-list/hotel-booking-list.component').then(
                                (m) => m.HotelBookingListComponent
                            )
                    }
                ]
            },
            {
                path: 'reservations',
                loadComponent: () => import('@app/pages/reservations/reservation-list/reservation-list.component').then((m) => m.ReservationListComponent)
            },
            {
                path: 'ota-performances',
                loadComponent: () => import('@app/pages/dashboard/tables/ota-performance/ota-performance.component').then((m) => m.OtaPerformanceComponent)
            }
        ]
    },

    { path: 'login', component: LoginComponent },
    { path: 'notfound', component: NotfoundComponent },
    { path: 'not-authorized', component: NotAuthorizedComponent },
    { path: '**', redirectTo: '/notfound' }
];
